import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { healthCyclesAPI } from '../../services/api'
import GoalChangePreview from './GoalChangePreview'
import RevisionChangeSummary from './RevisionChangeSummary'

// VH-227: shows what finalizing freezes and changes outside the cycle before
// the user commits to it. Finalizing is not reversible.
export function FinalizeDialog({ cycleId, open, onClose, onConfirm, busy = false, error = null }) {
  const { t } = useTranslation()
  const { data: preview, isLoading } = useQuery({
    queryKey: ['healthCycleFinalizePreview', cycleId],
    queryFn: () => healthCyclesAPI.finalizePreview(cycleId),
    select: (response) => response.data,
    enabled: open && !!cycleId,
    // A preview taken while steps ran would otherwise block Finalize until reopened.
    refetchInterval: (query) => (open && query.state.data?.data?.running ? 3000 : false),
  })

  const goalEffect = preview?.effects?.find((e) => e.code === 'goal_from_nutrition_targets')
  const otherEffects = (preview?.effects ?? []).filter((e) => e.code !== 'goal_from_nutrition_targets')
  const blocked = !preview || preview.running || preview.already_finalized

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-lg rounded-xl bg-surface-container p-6 shadow-xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="text-lg font-semibold text-gray-900">
            {t('healthCycle.finalize.title', { number: preview?.version_number ?? '' })}
          </DialogTitle>
          {isLoading ? (
            <p className="mt-4 text-sm text-gray-500">{t('common.loading')}</p>
          ) : preview ? (
            <div className="mt-4 space-y-4 text-sm">
              {preview.running && <p className="text-yellow-800">{t('healthCycle.finalize.running')}</p>}
              {preview.already_finalized && <p className="text-gray-700">{t('healthCycle.finalize.alreadyFinalized')}</p>}
              <div>
                <p className="font-medium text-gray-900">{t('healthCycle.finalize.frozen')}</p>
                <p className="text-gray-600">{preview.frozen.map((s) => t(`healthCycle.sections.${s}`, s)).join(', ')}</p>
              </div>
              <div>
                <p className="font-medium text-gray-900">{t('healthCycle.finalize.notFrozen')}</p>
                <p className="text-gray-600">{preview.not_frozen.map((s) => t(`healthCycle.sections.${s}`, s)).join(', ')}</p>
              </div>
              {otherEffects.length > 0 && (
                <ul className="list-disc pl-5 text-gray-700">
                  {otherEffects.map((e) => (
                    <li key={e.code}>{t(`healthCycle.finalize.effects.${e.code}`, { number: e.version_number, defaultValue: e.detail || e.code })}</li>
                  ))}
                </ul>
              )}
              {/* VH-233: what the revision changes against the finalized baseline */}
              {!preview.running && <RevisionChangeSummary cycleId={cycleId} versionNumber={preview.version_number} enabled={open} />}
              <GoalChangePreview effect={goalEffect} />
              <p className="text-xs text-gray-500">{t('healthCycle.finalize.irreversible')}</p>
            </div>
          ) : null}
          {error && <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>}
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" className="btn-outline" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn-primary" disabled={blocked || busy} onClick={onConfirm}>
              {t('healthCycle.finalize.confirm')}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}

export default FinalizeDialog
